// frontend/src/components/profile/ProviderDashboard.js
import React, { useState, useEffect } from 'react';
import { serviceOpsApi } from '../../api/serviceOps';
import helpers from '../../utils/helpers';
import { FaTools, FaClock, FaCar, FaMapMarkerAlt, FaTimes, FaClipboardCheck, FaInfoCircle, FaFileInvoiceDollar, FaSpinner } from 'react-icons/fa';

function ProviderDashboard() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- MODAL STATES ---
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [processing, setProcessing] = useState(false);

  // --- QUOTE FORM STATE ---
  const [quoteItems, setQuoteItems] = useState([{ name: '', quantity: 1, unit_price: '', type: 'part' }]);
  const [quoteNotes, setQuoteNotes] = useState('');

  // --- COMPLETION FORM STATE ---
  const [completionData, setCompletionData] = useState({
      service_date: new Date().toISOString().split('T')[0],
      mileage_at_service: '',
      faults_fixed: '',
      work_done_description: '',
      labor_cost: '',
      parts_used: [] 
  });
  const [newPart, setNewPart] = useState({ part_name: '', quantity: 1, unit_price: '' });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const data = await serviceOpsApi.getProviderJobs();
      setJobs(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load assigned jobs.");
    } finally {
      setLoading(false);
    }
  };

  // --- QUOTE HANDLERS ---
  const openQuoteModal = (job) => {
      setSelectedJob(job);
      setQuoteItems([{ name: 'Labor / Diagnosis', quantity: 1, unit_price: '', type: 'labor' }]);
      setQuoteNotes('');
      setQuoteModalOpen(true);
  };

  const addQuoteItem = () => {
      setQuoteItems([...quoteItems, { name: '', quantity: 1, unit_price: '', type: 'part' }]);
  };

  const removeQuoteItem = (index) => {
      setQuoteItems(quoteItems.filter((_, i) => i !== index));
  };

  const updateQuoteItem = (index, field, value) => {
      const updated = [...quoteItems];
      updated[index][field] = value;
      setQuoteItems(updated);
  };

  const handleSendQuote = async (e) => {
      e.preventDefault();
      setProcessing(true);
      try {
          const payload = {
              items: quoteItems.map(i => ({
                  ...i,
                  unit_price: parseFloat(i.unit_price),
                  quantity: parseInt(i.quantity)
              })),
              notes: quoteNotes
          };
          await serviceOpsApi.createQuote(selectedJob.id, payload);
          alert("Quote sent to customer for approval.");
          setQuoteModalOpen(false);
          fetchJobs();
      } catch (err) {
          alert("Failed to send quote.");
      } finally {
          setProcessing(false);
      }
  };

  // --- COMPLETION HANDLERS ---
  const openCompleteModal = (job) => {
      setSelectedJob(job);
      setCompletionData({
          service_date: new Date().toISOString().split('T')[0],
          mileage_at_service: '',
          faults_fixed: '',
          work_done_description: '',
          labor_cost: '',
          parts_used: []
      });
      setCompleteModalOpen(true);
  };

  const addPart = () => {
      if (!newPart.part_name || !newPart.unit_price) return;
      setCompletionData(prev => ({
          ...prev,
          parts_used: [...prev.parts_used, { ...newPart, unit_price: parseFloat(newPart.unit_price) }]
      }));
      setNewPart({ part_name: '', quantity: 1, unit_price: '' });
  };

  const removePart = (index) => {
      setCompletionData(prev => ({
          ...prev,
          parts_used: prev.parts_used.filter((_, i) => i !== index)
      }));
  };

  const handleCompleteJob = async (e) => {
      e.preventDefault();
      setProcessing(true);
      
      const partsTotal = completionData.parts_used.reduce((sum, p) => sum + (p.unit_price * p.quantity), 0);
      const totalCost = parseFloat(completionData.labor_cost) + partsTotal;

      const payload = {
          vehicle_id: selectedJob.vehicle_id,
          provider_id: selectedJob.assigned_provider_id,
          service_date: completionData.service_date,
          mileage_at_service: parseInt(completionData.mileage_at_service),
          faults_fixed: completionData.faults_fixed,
          work_done_description: completionData.work_done_description,
          labor_cost: parseFloat(completionData.labor_cost),
          parts_used: completionData.parts_used,
          total_cost: totalCost
      };

      try {
          await serviceOpsApi.completeJob(selectedJob.id, payload);
          alert("Job Completed! Receipt sent to customer.");
          setCompleteModalOpen(false);
          fetchJobs();
      } catch (err) {
          console.error(err);
          alert("Failed to complete job.");
      } finally {
          setProcessing(false);
      }
  };

  if (loading) return <div className="text-center py-10">Loading jobs...</div>;

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <FaTools className="text-primary" /> Assigned Jobs
      </h2>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg shadow-sm flex items-start gap-3">
          <FaInfoCircle className="text-blue-500 text-xl mt-1 flex-shrink-0" />
          <div>
              <h4 className="font-bold text-blue-800 text-sm">Workflow</h4>
              <p className="text-sm text-blue-700">
                  1. <strong>Diagnose:</strong> Check the vehicle.<br/>
                  2. <strong>Quote:</strong> Send an estimate (Parts + Labor) to the customer.<br/>
                  3. <strong>Wait:</strong> Do not start work until status is <strong>Work In Progress</strong>.<br/>
                  4. <strong>Complete:</strong> Finish the job and generate the final invoice.
              </p>
          </div>
      </div>

      {error && <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>}

      {jobs.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p className="text-gray-500">You have no active jobs assigned.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map(job => (
            <div key={job.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-bold text-gray-800">{job.vehicle_info_snapshot}</h3>
                  <p className="text-sm text-gray-600">Customer: {job.user_name} ({job.user_phone || 'No Phone'})</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                    job.service_stage === 'completed' ? 'bg-green-100 text-green-800' : 
                    job.service_stage === 'work_in_progress' ? 'bg-blue-100 text-blue-800' :
                    job.service_stage === 'quote_sent' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-purple-100 text-purple-800'
                }`}>
                  {job.service_stage.replace('_', ' ')}
                </span>
              </div>

              <div className="text-sm text-gray-600 mb-4">
                <p className="flex items-center gap-2"><FaClock className="text-gray-400" /> {job.preferred_date} ({job.preferred_time_slot})</p>
                <p className="flex items-center gap-2 mt-1"><FaCar className="text-gray-400" /> Issue: {job.description}</p>
                {job.service_type === 'home_service' && (
                    <p className="flex items-center gap-2 mt-1 text-blue-600"><FaMapMarkerAlt /> {job.address}</p>
                )}
              </div>

              {/* ACTION BUTTONS */}
              <div className="flex gap-2">
                  {/* 1. CREATE QUOTE (If Scheduled or Diagnosing) */}
                  {(job.service_stage === 'scheduled' || job.service_stage === 'quote_rejected') && (
                      <button 
                        onClick={() => openQuoteModal(job)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 rounded flex items-center justify-center gap-2"
                      >
                        <FaFileInvoiceDollar /> Create Quote
                      </button>
                  )}

                  {/* 2. COMPLETE JOB (Only if Work In Progress) */}
                  {job.service_stage === 'work_in_progress' && (
                      <button 
                        onClick={() => openCompleteModal(job)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-2 rounded flex items-center justify-center gap-2"
                      >
                        <FaClipboardCheck /> Complete Job
                      </button>
                  )}

                  {/* 3. WAITING STATE */}
                  {job.service_stage === 'quote_sent' && (
                      <div className="w-full bg-yellow-50 text-yellow-800 text-center py-2 rounded font-bold text-sm border border-yellow-200">
                          Waiting for Customer Approval...
                      </div>
                  )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- QUOTE MODAL --- */}
      {quoteModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                  <div className="flex justify-between items-center p-6 border-b">
                      <h3 className="text-xl font-bold">Create Service Quote</h3>
                      <button onClick={() => setQuoteModalOpen(false)}><FaTimes /></button>
                  </div>
                  
                  <form onSubmit={handleSendQuote} className="overflow-y-auto p-6 space-y-4">
                      <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                          List all parts and labor required. The customer must approve this total before you start work.
                      </div>

                      {quoteItems.map((item, idx) => (
                          <div key={idx} className="flex gap-2 items-center">
                              <input 
                                  placeholder="Item Description" 
                                  value={item.name} 
                                  onChange={(e) => updateQuoteItem(idx, 'name', e.target.value)} 
                                  className="flex-grow p-2 border rounded"
                                  required
                              />
                              <input 
                                  type="number" 
                                  placeholder="Qty" 
                                  value={item.quantity} 
                                  onChange={(e) => updateQuoteItem(idx, 'quantity', e.target.value)} 
                                  className="w-16 p-2 border rounded"
                                  min="1"
                                  required
                              />
                              <input 
                                  type="number" 
                                  placeholder="Price" 
                                  value={item.unit_price} 
                                  onChange={(e) => updateQuoteItem(idx, 'unit_price', e.target.value)} 
                                  className="w-24 p-2 border rounded"
                                  required
                              />
                              <select 
                                  value={item.type} 
                                  onChange={(e) => updateQuoteItem(idx, 'type', e.target.value)} 
                                  className="p-2 border rounded bg-white"
                              >
                                  <option value="part">Part</option>
                                  <option value="labor">Labor</option>
                              </select>
                              <button type="button" onClick={() => removeQuoteItem(idx)} className="text-red-500"><FaTimes /></button>
                          </div>
                      ))}
                      
                      <button type="button" onClick={addQuoteItem} className="text-blue-600 font-bold text-sm">+ Add Item</button>

                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Notes (Optional)</label>
                          <textarea 
                              value={quoteNotes} 
                              onChange={(e) => setQuoteNotes(e.target.value)} 
                              className="w-full p-2 border rounded" 
                              rows="2"
                              placeholder="e.g. Parts may take 2 days to arrive..."
                          ></textarea>
                      </div>

                      <div className="flex justify-between items-center pt-4 border-t">
                          <div className="text-lg font-bold">
                              Total: {helpers.formatCurrency(quoteItems.reduce((sum, i) => sum + (i.unit_price * i.quantity), 0), 'NGN')}
                          </div>
                          <div className="flex gap-2">
                              <button type="button" onClick={() => setQuoteModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                              <button type="submit" disabled={processing} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                                  {processing ? <FaSpinner className="animate-spin" /> : 'Send Quote'}
                              </button>
                          </div>
                      </div>
                  </form>
              </div>
          </div>
      )}

      {/* --- COMPLETE JOB MODAL (Existing) --- */}
      {completeModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                  <div className="flex justify-between items-center p-6 border-b">
                      <h3 className="text-xl font-bold">Complete Job Report</h3>
                      <button onClick={() => setCompleteModalOpen(false)}><FaTimes /></button>
                  </div>
                  
                  <form onSubmit={handleCompleteJob} className="overflow-y-auto p-6 space-y-4">
                      {/* ... (Same completion form as before) ... */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                              <label className="block text-xs font-bold text-gray-600 mb-1">Service Date</label>
                              <input type="date" required value={completionData.service_date} onChange={(e) => setCompletionData({...completionData, service_date: e.target.value})} className="w-full p-2 border rounded" />
                          </div>
                          <div>
                              <label className="block text-xs font-bold text-gray-600 mb-1">Mileage at Service</label>
                              <input type="number" required value={completionData.mileage_at_service} onChange={(e) => setCompletionData({...completionData, mileage_at_service: e.target.value})} className="w-full p-2 border rounded" />
                          </div>
                      </div>

                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Faults Fixed</label>
                          <input type="text" required value={completionData.faults_fixed} onChange={(e) => setCompletionData({...completionData, faults_fixed: e.target.value})} className="w-full p-2 border rounded" placeholder="e.g. Worn Brake Pads, Oil Leak" />
                      </div>

                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Work Done Description</label>
                          <textarea required value={completionData.work_done_description} onChange={(e) => setCompletionData({...completionData, work_done_description: e.target.value})} className="w-full p-2 border rounded h-20" placeholder="Detailed description of work..."></textarea>
                      </div>

                      {/* PARTS USED SECTION */}
                      <div className="bg-gray-50 p-4 rounded border">
                          <label className="block text-xs font-bold text-gray-600 mb-2">Parts Used (Final)</label>
                          {completionData.parts_used.map((part, idx) => (
                              <div key={idx} className="flex justify-between items-center bg-white p-2 mb-2 rounded border text-sm">
                                  <span>{part.quantity}x {part.part_name}</span>
                                  <div className="flex items-center gap-3">
                                      <span>{helpers.formatCurrency(part.unit_price * part.quantity, 'NGN')}</span>
                                      <button type="button" onClick={() => removePart(idx)} className="text-red-500"><FaTimes /></button>
                                  </div>
                              </div>
                          ))}
                          
                          <div className="flex gap-2 mt-2">
                              <input placeholder="Part Name" value={newPart.part_name} onChange={(e) => setNewPart({...newPart, part_name: e.target.value})} className="flex-grow p-2 border rounded text-sm" />
                              <input type="number" placeholder="Qty" value={newPart.quantity} onChange={(e) => setNewPart({...newPart, quantity: parseInt(e.target.value)})} className="w-16 p-2 border rounded text-sm" />
                              <input type="number" placeholder="Price" value={newPart.unit_price} onChange={(e) => setNewPart({...newPart, unit_price: e.target.value})} className="w-24 p-2 border rounded text-sm" />
                              <button type="button" onClick={addPart} className="bg-blue-600 text-white px-3 rounded text-sm">Add</button>
                          </div>
                      </div>

                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Labor Cost (NGN)</label>
                          <input type="number" required value={completionData.labor_cost} onChange={(e) => setCompletionData({...completionData, labor_cost: e.target.value})} className="w-full p-2 border rounded" />
                      </div>

                      <div className="flex justify-end gap-3 pt-4 border-t">
                          <button type="button" onClick={() => setCompleteModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                          <button type="submit" disabled={processing} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                              {processing ? 'Processing...' : 'Complete Job'}
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}
    </div>
  );
}

export default ProviderDashboard;